import React from "react";
import {
  SFStack,
  SFDialog,
  SFTypography,
  SFUseStyled,
  SFHelpers,
  SFIcons,
  SFToggleButtons,
  SFButton,
} from "@sf-core";
import { useTranslation } from "react-i18next";
import getStatusBadge from "../../constants/statusBadgeMappings";

const { styled } = SFUseStyled();

const StyledStack = styled(SFStack)(({ theme }) => ({
  paddingTop: theme.spacing(7),
  paddingBottom: theme.spacing(3),
  position: "relative",
}));

const LockContainer = styled(SFStack)(({ theme }) => ({
  backgroundColor: theme.palette.black.main,
  borderRadius: "50%",
  height: "100%",
  padding: theme.spacing(1),
}));

const BadgeContainer = styled(SFStack, {
  shouldForwardProp: (prop) => prop !== "color",
})(({ theme, color }) => ({
  position: "absolute",
  left: theme.spacing(0.5),
  top: theme.spacing(0.5),
  padding: theme.spacing(0.5),
  paddingTop: theme.spacing(0.25),
  paddingBottom: theme.spacing(0.25),
  backgroundColor: theme.palette[color].main,
  borderRadius: theme.spacing(0.5),
}));

const DurationContainer = styled(SFStack, {
  shouldForwardProp: (prop) => prop !== "badge",
})(({ theme, badge }) => ({
  position: "absolute",
  left: theme.spacing(0.5),
  top: theme.spacing(0.5),
  padding: theme.spacing(0.62),
  backgroundColor: "rgba(255, 255, 255, 0.2)",
  color: theme.palette.grey200.main,
  borderRadius: theme.spacing(0.5),
  ...(badge && {
    left: theme.spacing(12),
  }),
}));

const CourseDetailsDialog = ({
  course,
  showCourseDetailsDialog,
  setShowCourseDetailsDialog,
  handleThumbnailClick,
}) => {
  const { t } = useTranslation();
  const badge = getStatusBadge(course?.status, t);
  return (
    <SFDialog
      size={"md"}
      open={showCourseDetailsDialog}
      onClose={() => setShowCourseDetailsDialog(false)}
      backgroundThumbnail={course.thumbnail}
      lightBackgroundDialog={true}
      contentZeroPadding
      header={
        <StyledStack>
          <SFStack direction="row">
            <DurationContainer badge={badge}>
              <SFStack direction="row" spacing={0.5}>
                <SFIcons.SFTimerIcon size={"small"} color="grey200" />
                <SFTypography variant="body1">
                  {SFHelpers.DurationFormatter(
                    course.duration,
                    SFHelpers.DurationFormatterOptions.ALL_UNITS
                  )}
                </SFTypography>
              </SFStack>
            </DurationContainer>
            {badge && (
              <BadgeContainer
                direction="row"
                spacing={0.5}
                alignItems="center"
                color={badge.color}
              >
                {badge.icon}
                <SFTypography variant="body1">{badge.label}</SFTypography>
              </BadgeContainer>
            )}
          </SFStack>
          <SFStack direction="column" spacing={4}>
            <SFTypography variant="detailsTotal" component="h1">{course.name}</SFTypography>
            <SFStack spacing={1}>
              <SFTypography variant="courseCarouselTitle">
                {t("courseDetailsDialog.description")}
              </SFTypography>
              <SFTypography variant="body2">
                {course.summary ? course.summary : course.description}
              </SFTypography>
            </SFStack>
            {course.skills && (
              <SFStack spacing={1}>
                <SFTypography variant="courseCarouselTitle">
                  {t("courseDetailsDialog.skills")}
                </SFTypography>
                <SFToggleButtons
                  size="small"
                  disabled={true}
                  items={course.skills.map((x) => ({
                    value: x.id,
                    label: x.name,
                  }))}
                  noHover
                />
              </SFStack>
            )}
          </SFStack>
        </StyledStack>
      }
      actionsBoxShadow={true}
      actionBtns={
        <SFStack direction="row" alignItems="center" spacing={2}>
          <SFButton
            variant="transparent"
            onClick={() => setShowCourseDetailsDialog(false)}
          >
            <SFTypography>{t("courseDetailsDialog.buttonClose")}</SFTypography>
          </SFButton>
          {course.locked ? (
            <SFStack direction="row" alignItems="center" spacing={1}>
              <LockContainer>
                <SFIcons.SFLockIcon size="large" color="grey200" />
              </LockContainer>
              <SFStack direction="column" alignItems="flex-start">
                <SFTypography variant="courseCarouselTitle">
                  {t("courseDetailsDialog.locked")}
                </SFTypography>
                <SFTypography variant="body2">
                  {t("courseDetailsDialog.lockedMessage")}
                </SFTypography>
              </SFStack>
            </SFStack>
          ) : (
            <SFButton
              variant="secondary"
              onClick={handleThumbnailClick}
              startIcon={<SFIcons.SFPlayIcon />}
            >
              <SFTypography>
                {course.status == "In progress"
                  ? t("courseDetailsDialog.buttonResume")
                  : t("courseDetailsDialog.buttonStart")}
              </SFTypography>
            </SFButton>
          )}
        </SFStack>
      }
    ></SFDialog>
  );
};

export default CourseDetailsDialog;
